// Large-run filter dialog, shared by the Data Table and the Geography map.
// Runs with more than LARGE_RUN_THRESHOLD region hits open this first so the
// user can narrow the alignments before Read Hits loads them.
import React from 'react';
import axios from 'axios';
import { apiPath } from '../../apiurl.js';
import './LargeRunModal.css';

export const LARGE_RUN_THRESHOLD = 75000;

/** Read Hits page path for one run, optionally filtered to gene regions. */
export function readHitsPath(run, { regions = [], timeFrame } = {}) {
  const p = new URLSearchParams({ run });
  if (regions.length) p.set('sseqregion', regions.join(','));
  if (timeFrame) p.set('timeFrame', timeFrame);
  return `/pass/ReadHits?${p.toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
//  LARGE RUN FILTER MODAL
// ─────────────────────────────────────────────────────────────────────────────

const PIDENT_PRESETS = [
  { label: '75%',  value: 75,  desc: 'All alignments (default)' },
  { label: '80%',  value: 80,  desc: 'Likely poliovirus threshold' },
  { label: '90%',  value: 90,  desc: 'High confidence' },
  { label: '95%',  value: 95,  desc: 'Very high confidence' },
  { label: '96%',  value: 96,  desc: 'Poliovirus zone' },
];

const LEN_OPTIONS = [
  { label: 'Any',      value: 0   },
  { label: '≥ 50 bp',  value: 50  },
  { label: '≥ 100 bp', value: 100 },
  { label: '≥ 200 bp', value: 200 },
];

const POLTYPE_FALLBACK = ['PV1', 'PV2', 'PV3'];

/**
 * Pre-load filter dialog for runs too large to open in Read Hits directly.
 * @param data            { run, count } or null (closed)
 * @param onNavigate      (path) => void
 * @param initialRegions  optional region names (e.g. ['VP1']) to pre-select,
 *                        kept only if the run actually has those regions
 */
export default function LargeRunModal({ data, onClose, onNavigate, initialRegions }) {
  const [minPident,        setMinPident]        = React.useState(75);
  const [minLen,           setMinLen]           = React.useState(0);
  const [filterPolytypes,  setFilterPolytypes]  = React.useState(false);
  const [polytypes,        setPolytypes]        = React.useState([]);
  const [filterSseqRegion,   setFilterSseqRegion]   = React.useState(false);
  const [sseqRegions,        setSseqRegions]        = React.useState([]);
  const [distinctData,       setDistinctData]       = React.useState(null);
  const [distinctLoading,    setDistinctLoading]    = React.useState(false);

  React.useEffect(() => {
    if (!data) return;
    setDistinctData(null);
    setDistinctLoading(true);
    axios
      .get(`${apiPath}/api/readhit/${data.run}/distinct`)
      .then((res) => {
        const d = res.data;
        setDistinctData(d);
        setPolytypes(d.polytypes?.length > 0 ? d.polytypes : POLTYPE_FALLBACK);
        const available = d.sseqRegions ?? [];
        const preset = (initialRegions ?? []).filter(r => available.includes(r));
        if (preset.length > 0) {
          setSseqRegions(preset);
          setFilterSseqRegion(true);
        } else {
          setSseqRegions(available);
          setFilterSseqRegion(false);
        }
      })
      .catch(() => {
        setPolytypes(POLTYPE_FALLBACK);
        setSseqRegions([]);
      })
      .finally(() => setDistinctLoading(false));
  }, [data?.run]); // eslint-disable-line

  if (!data) return null;

  const availablePolytypes   = distinctData?.polytypes?.length  > 0 ? distinctData.polytypes  : POLTYPE_FALLBACK;
  const availableSseqRegions = distinctData?.sseqRegions ?? [];

  const togglePoltype    = (pt) => setPolytypes((p)   => p.includes(pt) ? p.filter((x) => x !== pt) : [...p, pt]);
  const toggleSseqRegion = (r)  => setSseqRegions((p) => p.includes(r)  ? p.filter((x) => x !== r)  : [...p, r]);

  const hasActiveFilters =
    minPident > 75 ||
    minLen > 0 ||
    (filterPolytypes  && polytypes.length   < availablePolytypes.length)   ||
    (filterSseqRegion && sseqRegions.length < availableSseqRegions.length  && sseqRegions.length > 0);

  const handleLoadFiltered = () => {
    const params = new URLSearchParams({ run: data.run });
    if (minPident > 75) params.set('minPident', String(minPident));
    if (minLen > 0) params.set('minLen', String(minLen));
    if (filterPolytypes && polytypes.length > 0 && polytypes.length < availablePolytypes.length)
      params.set('poltype', polytypes.join(','));
    if (filterSseqRegion && sseqRegions.length > 0 && sseqRegions.length < availableSseqRegions.length)
      params.set('sseqregion', sseqRegions.join(','));
    onNavigate(`/pass/ReadHits?${params.toString()}`);
    onClose();
  };

  const handleLoadAll = () => { onNavigate(`/pass/ReadHits?run=${data.run}`); onClose(); };

  const isDisabled =
    (filterPolytypes  && polytypes.length === 0) ||
    (filterSseqRegion && sseqRegions.length === 0);

  return (
    <div className="lrf-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lrf-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="lrf-header">
          <div className="lrf-header-left">
            <span className="lrf-warn-icon">⚠</span>
            <div>
              <div className="lrf-title">Large Dataset Detected</div>
              <div className="lrf-run-id">{data.run}</div>
            </div>
          </div>
          <button className="lrf-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Count bar ── */}
        <div className="lrf-count-bar">
          <div className="lrf-count-num">{Number(data.count).toLocaleString()}</div>
          <div className="lrf-count-label">alignment hits in this run</div>
          <div className="lrf-count-cap">
            Only the first <strong>75,000 rows</strong> will be loaded without filters.
            Use filters below to narrow the dataset and focus your analysis.
          </div>
        </div>

        {/* ── Distinct-values loading ── */}
        {distinctLoading && (
          <div className="lrf-distinct-loading">
            <span className="lrf-distinct-spinner" /> Loading filter options from dataset…
          </div>
        )}

        {/* ── Filters ── */}
        <div className="lrf-filters">

          {/* Min % Identity */}
          <div className="lrf-filter-group">
            <div className="lrf-filter-label">
              Min % Identity
              <span className="lrf-filter-val">{minPident}%</span>
              {distinctData?.pidentRange && (
                <span className="lrf-filter-hint">
                  &nbsp;(data: {distinctData.pidentRange.min}–{distinctData.pidentRange.max}%)
                </span>
              )}
            </div>
            <input
              type="range"
              className="lrf-slider"
              min={75} max={100} step={1}
              value={minPident}
              onChange={(e) => setMinPident(Number(e.target.value))}
            />
            <div className="lrf-presets">
              {PIDENT_PRESETS.map((p) => (
                <button
                  key={p.value}
                  className={`lrf-preset-btn${minPident === p.value ? ' active' : ''}`}
                  onClick={() => setMinPident(p.value)}
                  title={p.desc}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Min alignment length */}
          <div className="lrf-filter-group">
            <div className="lrf-filter-label">
              Min Alignment Length
              {distinctData?.lenRange && (
                <span className="lrf-filter-hint">
                  &nbsp;(data: {distinctData.lenRange.min}–{distinctData.lenRange.max} bp)
                </span>
              )}
            </div>
            <div className="lrf-radio-group">
              {LEN_OPTIONS.map((opt) => (
                <label key={opt.value} className="lrf-radio-item">
                  <input
                    type="radio"
                    name="lrf-minlen"
                    checked={minLen === opt.value}
                    onChange={() => setMinLen(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Poliovirus types — data-driven */}
          <div className="lrf-filter-group">
            <label className="lrf-toggle-row">
              <input
                type="checkbox"
                checked={filterPolytypes}
                onChange={(e) => setFilterPolytypes(e.target.checked)}
              />
              <span className="lrf-filter-label" style={{ marginBottom: 0 }}>
                Filter by Poliovirus Type
                {distinctData?.polytypes?.length > 0 && (
                  <span className="lrf-filter-hint"> ({distinctData.polytypes.length} unique)</span>
                )}
              </span>
            </label>
            {filterPolytypes && (
              <div className="lrf-checkbox-group">
                {availablePolytypes.map((pt) => (
                  <label key={pt} className={`lrf-check-item${polytypes.includes(pt) ? ' checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={polytypes.includes(pt)}
                      onChange={() => togglePoltype(pt)}
                    />
                    <span>{pt}</span>
                  </label>
                ))}
                {polytypes.length === 0 && (
                  <span className="lrf-check-warn">Select at least one type</span>
                )}
              </div>
            )}
          </div>

          {/* Gene region (sseqid prefix) — data-driven: 5UTR, 2C, VP1, VP2 … */}
          {!distinctLoading && availableSseqRegions.length > 0 && (
            <div className="lrf-filter-group">
              <label className="lrf-toggle-row">
                <input type="checkbox" checked={filterSseqRegion} onChange={(e) => setFilterSseqRegion(e.target.checked)} />
                <span className="lrf-filter-label" style={{ marginBottom: 0 }}>
                  Filter by Genome Region (sseqid)
                  <span className="lrf-filter-hint"> ({availableSseqRegions.length} regions in this run)</span>
                </span>
              </label>
              {filterSseqRegion && (
                <div className="lrf-checkbox-group lrf-sseqid-group">
                  <div className="lrf-sseqid-actions">
                    <button className="lrf-sseqid-btn" onClick={() => setSseqRegions([...availableSseqRegions])}>All</button>
                    <button className="lrf-sseqid-btn" onClick={() => setSseqRegions([])}>None</button>
                  </div>
                  {availableSseqRegions.map((r) => (
                    <label key={r} className={`lrf-check-item${sseqRegions.includes(r) ? ' checked' : ''}`}>
                      <input type="checkbox" checked={sseqRegions.includes(r)} onChange={() => toggleSseqRegion(r)} />
                      <span className="lrf-sseqid-label">{r}</span>
                    </label>
                  ))}
                  {sseqRegions.length === 0 && <span className="lrf-check-warn">Select at least one region</span>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="lrf-footer">
          <button className="lrf-btn-ghost" onClick={handleLoadAll} disabled={distinctLoading}>
            Load All <span className="lrf-btn-sub">(75k cap, no filters)</span>
          </button>
          <button
            className="lrf-btn-primary"
            onClick={handleLoadFiltered}
            disabled={isDisabled || distinctLoading}
          >
            {hasActiveFilters ? 'Apply Filters & View →' : 'View without Filters →'}
          </button>
        </div>
      </div>
    </div>
  );
}

